import { radioAnatomy as parts } from '@chakra-ui/anatomy'
import type { RadioProps } from '@chakra-ui/react'
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleFunction,
} from '@chakra-ui/theme-tools'

import { Checkbox } from './Checkbox'

const baseStyleControl: SystemStyleFunction = (props) => {
  const { control = {} } = Checkbox.baseStyle(props)
  const { colorScheme: c } = props

  return {
    ...control,
    borderRadius: 'full',
    _hover: {
      borderColor: `${c}.500`,
      _disabled: {
        borderColor: 'white',
      }
    },
    _checked: {
      ...(control as RadioProps)._checked,
      bg: `${c}.500`,
      border: '1px solid',
      borderColor: 'white',
      _before: {
        content: null,
      },
      _disabled: {
        bg: `${c}.200`,
      }
    },
  }
}

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  label: Checkbox.baseStyle(props).label,
  container: Checkbox.baseStyle(props).container,
  control: baseStyleControl(props),
})

const sizes: Record<string, PartsStyleObject<typeof parts>> = {
  sm: {
    control: {
      width: 3,
      height: 3,
      _checked: {
        _before: {
          w: 2,
          h: 2,
        },
      },
    },
    label: { fontSize: 'sm' },
  },
  md: {
    control: { w: 4, h: 4 },
    label: { fontSize: 'md' },
  },
  lg: {
    control: { w: 5, h: 5 },
    label: { fontSize: 'lg' },
  },
}

const variantSimple: PartsStyleFunction<typeof parts> = (props) => {
  return {
    container: {
      mr: 2,
      bg: 'transparent',
    },
  }
}

const variantColor: PartsStyleFunction<typeof parts> = (props) => {
  return {
    control: {
      boxShadow: `0 0 0 3px var(--chakra-colors-${props.colorScheme}-100)`,
      borderColor: `${props.colorScheme}.500`,
      _hover: {
        borderColor: `${props.colorScheme}.600`,
        boxShadow: `0 0 0 3px var(--chakra-colors-${props.colorScheme}-100)`,
      },
      _focus: {
        boxShadow: `0 0 0 3px var(--chakra-colors-${props.colorScheme}-100)`,
      },
      _checked: {
        boxShadow: `0 0 0 3px var(--chakra-colors-${props.colorScheme}-100)`,
      },
    },
  }
}

const variants = {
  simple: variantSimple,
  color: variantColor,
}

const defaultProps = {
  size: 'md',
  variant: 'base',
  colorScheme: 'green',
}

export const Radio = {
  parts: parts.keys,
  baseStyle,
  sizes,
  variants,
  defaultProps,
}
